import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ChangeEvent, FC, useRef, useState } from 'react'
import classNames from 'classnames'
import { ReactComponent as BuildingIcon } from 'src/assets/svg/companyProfile/carbon_building.svg'
import { ReactComponent as PinIcon } from 'src/assets/svg/locationPin.svg'
import { StatusModal } from 'src/components/StatusModal'
import {
  PLATFORM_LABEL,
  PLATFORM_ORDER,
} from 'src/pages/ReviewAnalysis/constants'
import { getPlatformIcon } from 'src/pages/ReviewAnalysis/platformIcons'
import { useAddReviewAnalysisPlatformsBatchMutation } from 'src/reactQuery/useAddReviewAnalysisPlatformsBatchMutation'
import {
  AddReviewPlatformBatchEntry,
  ReviewPlatform,
} from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import styles from './connectPlatforms.module.scss'
import {
  detectSource,
  SourceInput,
  sourceKey,
  splitSources,
} from './detectPlatform'
import { PlatformSelect } from './PlatformSelect'
import { AnalysisGoalPicker } from '../AnalysisGoalPicker'

const PLATFORM_KIND: Record<ReviewPlatform, string> = {
  appstore: 'App reviews',
  booking: 'Guest reviews',
  expedia: 'Traveler reviews',
  google: 'Location reviews',
  playstore: 'App reviews',
  tripadvisor: 'Traveler reviews',
}

interface PendingLocation {
  id: string
  kind: 'link' | 'placeId'
  name: string
  platform: ReviewPlatform
  value: string
}

interface ConnectPlatformsProps {
  campaignId: string
  // Adding locations to an existing facility: its name is fixed.
  facility?: { id: string; name: string }
  // Absent on a campaign with no platforms yet — there is nowhere to go back to.
  onBack?: () => void
  onCreated: () => void
}

let nextId = 0

// Same wording as the Add Review Platform modal's failure.
const FAILURE_MESSAGE =
  'Something went wrong while adding the location. Please try again in a moment.'

/**
 * Connect Review Platforms: name a facility, pick the platforms to accept,
 * paste links / Place IDs or upload a CSV / TXT of them, review the detected
 * locations, then create them in one batch. Each link's platform is detected
 * from the link itself, and a link from an unticked platform ticks it.
 */
export const ConnectPlatforms: FC<ConnectPlatformsProps> = ({
  campaignId,
  facility,
  onBack,
  onCreated,
}) => {
  const [facilityName, setFacilityName] = useState(facility?.name ?? '')
  // The design preselects Brand Management; one goal is always chosen.
  const [category, setCategory] = useState('Brand Management')
  const [selected, setSelected] = useState<ReviewPlatform[]>([])
  const [tab, setTab] = useState<'paste' | 'upload'>('paste')
  const [pasted, setPasted] = useState('')
  const [locations, setLocations] = useState<PendingLocation[]>([])
  const [notice, setNotice] = useState<{
    text: string
    warning: boolean
  } | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [created, setCreated] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  const allSelected = selected.length === PLATFORM_ORDER.length
  const togglePlatform = (p: ReviewPlatform) =>
    setSelected((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    )

  // Adds what it can and returns the inputs it could not take, with why. A
  // link's platform is ticked for it when it was not: making the user tick it
  // and add the link again read as the link silently not being added.
  const addSources = (inputs: SourceInput[]) => {
    const known = new Set(locations.map((l) => sourceKey(l.value)))
    const added: PendingLocation[] = []
    const unknown: string[] = []
    const ticked = new Set<ReviewPlatform>()
    let duplicates = 0
    for (const { name: givenName, text: input } of inputs) {
      const source = detectSource(input)
      const key = sourceKey(source.value)
      if (known.has(key)) {
        duplicates += 1
      } else if (!source.platform) {
        unknown.push(input)
      } else {
        if (!selected.includes(source.platform)) ticked.add(source.platform)
        known.add(key)
        added.push({
          id: `loc-${(nextId += 1)}`,
          kind: source.kind,
          name: givenName || source.name,
          platform: source.platform,
          value: source.value,
        })
      }
    }
    setLocations((prev) => [...prev, ...added])
    if (ticked.size) setSelected((prev) => [...prev, ...Array.from(ticked)])
    const parts: string[] = []
    if (added.length) parts.push(`${added.length} added`)
    if (ticked.size)
      parts.push(
        `${Array.from(ticked)
          .map((p) => PLATFORM_LABEL[p])
          .join(', ')} selected`
      )
    if (duplicates) parts.push(`${duplicates} already in the list`)
    if (unknown.length)
      parts.push(`${unknown.length} not recognised as a supported link`)
    setNotice(
      parts.length
        ? {
            text: `${parts.join(' · ')}.`,
            warning:
              unknown.length > 0 || (duplicates > 0 && added.length === 0),
          }
        : null
    )
    return unknown
  }

  const addPasted = () => {
    const inputs = splitSources(pasted)
    if (inputs.length === 0) {
      // e.g. a lone "Name, City" line read as a CSV header: say so, keep it
      setNotice({
        text: 'Nothing to add: paste one link or Place ID per line.',
        warning: true,
      })
      return
    }
    const rejected = addSources(inputs)
    // keep only what was not taken, so it can be fixed and retried
    setPasted(rejected.join('\n'))
  }

  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = () => {
      // what the file held but could not be added goes to the paste box, so
      // it can be fixed and retried rather than lost
      const rejected = addSources(splitSources(String(reader.result ?? '')))
      if (rejected.length) {
        setPasted((prev) => [prev, ...rejected].filter(Boolean).join('\n'))
        setTab('paste')
      }
    }
    reader.readAsText(file)
  }

  const updateLocation = (id: string, patch: Partial<PendingLocation>) =>
    setLocations((prev) =>
      prev.map((l) => (l.id === id ? { ...l, ...patch } : l))
    )

  const { isPending, mutate } = useAddReviewAnalysisPlatformsBatchMutation(
    campaignId,
    {
      onError: () => setFailure(FAILURE_MESSAGE),
      onSuccess: ({ results }) => {
        const failed = results.filter((r) => r.status === 'failed')
        if (failed.length) {
          // Results come back in request order. The saved ones leave the
          // list, so Try Again resubmits only what failed.
          setLocations((prev) =>
            prev.filter((_, i) => results[i]?.status === 'failed')
          )
          setFailure(
            failed.length === results.length
              ? FAILURE_MESSAGE
              : `${results.length - failed.length} of ${results.length} locations were added, ${failed.length} could not be. Please try again in a moment.`
          )
          return
        }
        setCreated(true)
      },
    }
  )

  const name = facilityName.trim()
  const canCreate =
    !!name &&
    locations.length > 0 &&
    locations.every((l) => l.name.trim()) &&
    !isPending

  const create = () => {
    const entries: AddReviewPlatformBatchEntry[] = locations.map((l) => ({
      facilityName: name,
      ...(facility ? { facilityId: facility.id } : {}),
      platform: l.platform,
      // the title is what ties collected reviews to this location
      title: l.name.trim(),
      ...(l.kind === 'placeId'
        ? { placeIds: [l.value] }
        : { dataSourceLinks: [l.value] }),
    }))
    mutate({
      platforms: entries,
      ...(!facility && category ? { facilityCategory: category } : {}),
    })
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          {onBack && (
            <button
              aria-label="Back"
              className={styles.backArrow}
              onClick={onBack}
              type="button"
            >
              ‹
            </button>
          )}
          <div>
            <h2 className={styles.title}>
              Review Analysis · Connect Review Platforms
            </h2>
            <p className={styles.subtitle}>
              Add your business locations and connect them to review platforms
              to start analysing customer feedback
            </p>
          </div>
        </div>

        <div className={styles.topRow}>
          <div className={styles.facilityColumn}>
            <label className={styles.fieldLabel} htmlFor="facility-name">
              <BuildingIcon className={styles.fieldIcon} />
              Facility Name
            </label>
            <input
              className={styles.textInput}
              disabled={!!facility}
              id="facility-name"
              onChange={(e) => setFacilityName(e.target.value)}
              placeholder="Enter a facility name"
              value={facilityName}
            />
            <p className={styles.hint}>
              This name will be used to identify your business in reports
            </p>
          </div>

          <div className={styles.platformColumn}>
            <div className={styles.platformHeader}>
              <div>
                <p className={styles.fieldLabel}>Select Review Platforms</p>
                <p className={styles.hint}>
                  Choose one or more platforms to collect reviews from
                </p>
              </div>
              <label className={styles.selectAll}>
                <input
                  checked={allSelected}
                  onChange={() =>
                    setSelected(allSelected ? [] : [...PLATFORM_ORDER])
                  }
                  type="checkbox"
                />
                Select All
              </label>
            </div>
            <div className={styles.platformGrid}>
              {PLATFORM_ORDER.map((p) => {
                const Icon = getPlatformIcon(p)
                const on = selected.includes(p)
                return (
                  <label
                    className={classNames(
                      styles.platformTile,
                      on && styles.platformTileOn
                    )}
                    key={p}
                  >
                    <Icon className={styles.platformIcon} />
                    <span className={styles.platformText}>
                      <span className={styles.platformName}>
                        {PLATFORM_LABEL[p]}
                      </span>
                      <span className={styles.platformKind}>
                        {PLATFORM_KIND[p]}
                      </span>
                    </span>
                    <input
                      checked={on}
                      className={styles.platformCheck}
                      onChange={() => togglePlatform(p)}
                      type="checkbox"
                    />
                  </label>
                )
              })}
            </div>
          </div>
        </div>

        {/* A new facility only: an existing one is edited from its card's
            menu. It tells the AI summary whose facility this is. */}
        {!facility && (
          <AnalysisGoalPicker onChange={setCategory} value={category} />
        )}

        <div className={styles.sourcePanel}>
          <div className={styles.tabs}>
            <button
              className={classNames(
                styles.tab,
                tab === 'paste' && styles.tabActive
              )}
              onClick={() => setTab('paste')}
              type="button"
            >
              Paste links
            </button>
            <button
              className={classNames(
                styles.tab,
                tab === 'upload' && styles.tabActive
              )}
              onClick={() => setTab('upload')}
              type="button"
            >
              Upload file
            </button>
          </div>

          <p className={styles.panelHint}>
            {tab === 'paste'
              ? "Paste one or more location links. You can also remove any you don't need"
              : 'Upload a CSV or TXT file with one link or Place ID per line'}
          </p>

          <div className={styles.sourceRow}>
            {tab === 'paste' ? (
              <textarea
                className={styles.textarea}
                onChange={(e) => setPasted(e.target.value)}
                placeholder={
                  'https://www.google.com/maps/place/...\nhttps://www.tripadvisor.com/Hotel_Review-...\nChIJ...'
                }
                rows={4}
                value={pasted}
              />
            ) : (
              <div className={styles.dropZone}>
                <input
                  accept=".csv,.txt,text/csv,text/plain"
                  className={styles.fileInput}
                  onChange={onFile}
                  ref={fileInput}
                  type="file"
                />
                <button
                  className={styles.chooseFile}
                  onClick={() => fileInput.current?.click()}
                  type="button"
                >
                  Choose file
                </button>
                <span className={styles.fileName}>
                  {fileName ?? 'No file chosen'}
                </span>
              </div>
            )}
            <div className={styles.formats}>
              <p className={styles.formatsTitle}>
                <FontAwesomeIcon
                  className={styles.formatsIcon}
                  icon={faInfoCircle}
                />
                Supported formats
              </p>
              <p className={styles.formatsText}>
                Google Maps, Tripadvisor, Booking.com, Google Play, App Store,
                Expedia links or Place IDs. You can add multiple links, one per
                line
              </p>
            </div>
          </div>

          {tab === 'paste' && (
            <button
              className={styles.addButton}
              disabled={!pasted.trim()}
              onClick={addPasted}
              type="button"
            >
              + Add Locations
            </button>
          )}
          {notice && (
            <p
              className={classNames(
                styles.notice,
                notice.warning && styles.noticeWarning
              )}
            >
              {notice.text}
            </p>
          )}
        </div>

        <div className={styles.addedHeader}>
          <p className={styles.fieldLabel}>
            Added locations ({locations.length})
          </p>
          <p className={styles.hint}>
            Your locations are saved and can be connected to one or more
            platforms. You can edit names, platforms or remove locations at any
            time
          </p>
        </div>

        <div className={styles.addedList}>
          {locations.length === 0 ? (
            <div className={styles.empty}>
              <span className={styles.emptyIcon}>
                <PinIcon />
              </span>
              <p className={styles.emptyTitle}>No location added yet</p>
              <p className={styles.hint}>
                You haven&apos;t added any locations yet. Add one to start
                collecting data
              </p>
            </div>
          ) : (
            locations.map((l) => {
              const Icon = getPlatformIcon(l.platform)
              return (
                <div className={styles.addedRow} key={l.id}>
                  <Icon className={styles.platformIcon} />
                  <input
                    className={classNames(
                      styles.textInput,
                      styles.nameInput,
                      !l.name.trim() && styles.inputMissing
                    )}
                    onChange={(e) =>
                      updateLocation(l.id, { name: e.target.value })
                    }
                    placeholder="Location name"
                    value={l.name}
                  />
                  <PlatformSelect
                    onChange={(platform) => {
                      updateLocation(l.id, { platform })
                      // keep the platform grid in step, as adding a link does
                      setSelected((prev) =>
                        prev.includes(platform) ? prev : [...prev, platform]
                      )
                    }}
                    value={l.platform}
                  />
                  <span className={styles.sourceValue} title={l.value}>
                    {l.value}
                  </span>
                  <button
                    aria-label="Remove location"
                    className={styles.removeButton}
                    onClick={() =>
                      setLocations((prev) => prev.filter((x) => x.id !== l.id))
                    }
                    type="button"
                  >
                    ×
                  </button>
                </div>
              )
            })
          )}
        </div>
      </div>

      <div className={styles.footer}>
        {onBack && (
          <button className={styles.backButton} onClick={onBack} type="button">
            Back
          </button>
        )}
        <button
          className={styles.createButton}
          disabled={!canCreate}
          onClick={create}
          type="button"
        >
          {isPending
            ? 'Creating…'
            : facility
              ? 'Add Locations'
              : 'Create Facility'}
        </button>
      </div>

      <StatusModal
        doneLabel="Try Again"
        hideCountdown
        isOpen={!!failure}
        message={failure ?? ''}
        onClose={() => setFailure(null)}
        onSecondaryAction={() => {
          setFailure(null)
          create()
        }}
        secondaryLabel="Discard"
        title="Failed to Create Facility"
        type="error"
      />
      <StatusModal
        hideCountdown
        isOpen={created}
        message="Everything is set. We'll begin monitoring these locations and notify you as soon as something important happens."
        onClose={() => {
          setCreated(false)
          onCreated()
        }}
        title={
          facility
            ? 'Locations Added Successfully'
            : 'Facility Created Successfully'
        }
        type="success"
      />
    </div>
  )
}
